import classNames from 'classnames';
import React, { useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { compose } from 'redux';
import { getOrganizationByIdSelector } from '@redux/organization/organizationSelectors';
import { duplicateEngagementPlanAction } from '@redux/tasks/tasksActions';
import { getEngagementPlansSelector } from '@redux/tasks/tasksSelector';
import { EngagementPlan } from '@redux/tasks/tasksTypes';
import { ReduxState } from '@redux/types';
import { NoActivePlansStub, NoFoundPlansStub } from 'components/EngagementPlanSelectionDrawer/components/NoPlansStub/NoPlansStub';
import { SearchField } from 'components/SearchField/SearchField';
import { Text } from 'components/Typography';
import { PlansListItem } from '../PlansListItem/PlansListItem';
import style from './PlansListSection.scss';

type CustomProps = {
  containerStyle?: string;
  selectedPlan: EngagementPlan | null;
  setSelectedPlan: (plan: EngagementPlan | null) => void;
  disablePlan: (plan: EngagementPlan) => void;
  organizationId: string;
  onDuplicateSuccess?: () => void;
  onDuplicateFail?: () => void;
};

type Props = ConnectedProps<typeof connectRedux> & CustomProps;

const PlansListSection: React.FC<Props> = ({
  containerStyle,
  engagementPlans,
  organizationId,
  organizationById,
  selectedPlan,
  setSelectedPlan,
  disablePlan,
  duplicateEngagementPlan,
  onDuplicateSuccess,
  onDuplicateFail
}) => {
  const [searchQuery, setSearchQuery] = useState('');

  const searchFilteredPlans = (engagementPlans || []).filter((plan) => plan.title?.toLowerCase().includes(searchQuery.toLowerCase()));
  const filteredPlans = searchFilteredPlans.reduce(
    (acc, plan) => {
      if (plan?.organizationIds?.includes(organizationId)) {
        acc.company.push(plan);
      } else {
        acc.global.push(plan);
      }
      return acc;
    },
    { global: [] as EngagementPlan[], company: [] as EngagementPlan[] }
  );

  const onDuplicateClick = (plan: EngagementPlan) => {
    const planCopy = {
      ...plan,
      id: '',
      title: `${plan.title} (1)`,
      description: plan.description || '',
      imageSource: '',
      organizationIds: [...(plan?.organizationIds || []), ...(!plan?.organizationIds?.includes(organizationId) ? [organizationId] : [])]
    };
    try {
      duplicateEngagementPlan(planCopy);
      onDuplicateSuccess?.();
    } catch (error) {
      console.log('Duplicate engagement plan was failed', error);
      onDuplicateFail?.();
    }
  };

  return (
    <div className={classNames(style.container, containerStyle)}>
      <div className={style.headerContainer}>
        <Text variant="nlg" weight={500}>
          All plans
        </Text>
        <SearchField searchText={searchQuery} setSearchText={setSearchQuery} placeholder="Search" />
      </div>
      <div className={style.listWrapper}>
        <div>
          <Text variant="nlg" weight={500}>
            Cairns Health ({filteredPlans.global.length})
          </Text>
          <div className={style.sectionList}>
            {filteredPlans.global.map((plan) => (
              <PlansListItem key={plan.id} plan={plan} isSelected={selectedPlan?.id === plan.id} onClick={setSelectedPlan} onDuplicateClick={onDuplicateClick} />
            ))}
            {!filteredPlans.global.length ? searchQuery.length ? <NoFoundPlansStub /> : !!engagementPlans.length && <NoActivePlansStub /> : null}
          </div>
        </div>

        <div>
          <Text variant="nlg" weight={500}>
            {`${organizationById[organizationId]?.name || ''} (${filteredPlans.company.length})`}
          </Text>
          <div className={classNames(style.sectionList, style.noBottomLine)}>
            {filteredPlans.company.map((plan) => (
              <PlansListItem
                key={plan.id}
                plan={plan}
                isSelected={selectedPlan?.id === plan.id}
                onClick={setSelectedPlan}
                onDuplicateClick={onDuplicateClick}
                onDisablePlan={disablePlan}
              />
            ))}
            {!filteredPlans.company.length ? searchQuery.length ? <NoFoundPlansStub /> : !!engagementPlans.length && <NoActivePlansStub /> : null}
          </div>
        </div>
      </div>
    </div>
  );
};

const connectRedux = connect(
  (state: ReduxState) => ({
    engagementPlans: getEngagementPlansSelector(state),
    organizationById: getOrganizationByIdSelector(state)
  }),
  {
    duplicateEngagementPlan: duplicateEngagementPlanAction
  }
);

export default compose(connectRedux)(PlansListSection);
